import React, { useEffect } from 'react'
import { Button } from 'vnetwork-ui'
import { useForm } from 'react-hook-form'
import { DayOffBODApprove } from '@/modules/Dayoffs/types/payload'
import { approveBodDayOff } from '@/modules/Dayoffs/services'
import toast from 'react-hot-toast'
import { IPermissionProps } from '@/types/shared'
import { DayOffResponse } from '@/modules/Dayoffs/types/response'
import { FormItem } from 'vnetwork-ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { bodApproveSchema } from './yup'
import { useRefetchDayOffQueries } from '@/modules/Dayoffs/constants'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface DayOffFormBODProps extends IPermissionProps {
    dayOff: DayOffResponse
    refetch: () => void
    onClose?: () => void
}
const BODFormConfirm: React.FC<DayOffFormBODProps> = ({
    dayOff,
    refetch,
    onClose,
}) => {
    const { refetchAll } = useRefetchDayOffQueries()
    const { t } = useTranslation('dayoffs')
    const { mutateAsync: BODConfirm, isPending: isBODConfirming } =
        useMutationWithHandler(
            {
                mutationFn: approveBodDayOff,
            },
            {
                reactQueryKeys: QUERY_KEYS.DAYOFFS,
                errorMessage: t('something_went_wrong'),
            }
        )

    const {
        control: controlBodForm,
        handleSubmit: handleBodConfirm,
        setValue: setBODFormValue,
    } = useForm<DayOffBODApprove>({
        defaultValues: {
            day_off_feedback_bod: t('approved'),
        },
        resolver: yupResolver(bodApproveSchema),
    })

    const onBodSubmit = async (dataReq: DayOffBODApprove) => {
        if (dataReq?.day_off_feedback_bod?.trim() === '') {
            toast.error(t('please_feedback'))
        }
        try {
            await BODConfirm({
                id: dayOff?.day_off_id || 0,
                feedback: {
                    day_off_feedback_bod: dataReq?.day_off_feedback_bod,
                },
            })
            if (onClose) {
                onClose()
            }
            refetch()
            refetchAll()
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (dayOff?.day_off_bod_confirmed) {
            setBODFormValue(
                'day_off_feedback_bod',
                dayOff?.day_off_feedback_bod
            )
        }
    }, [dayOff])

    return (
        <div className="w-full mb-2 bg-white rounded-lg p-4 text-sm">
            <form onSubmit={handleBodConfirm(onBodSubmit)}>
                <FormItem
                    control={controlBodForm}
                    disabled={dayOff?.day_off_status !== 'leader-approved'}
                    name="day_off_feedback_bod"
                    type="textarea"
                    placeholder={t('bod_feedback')}
                    label={t('bod_feedback')}
                />
                <div className="w-full flex justify-end gap-2">
                    {onClose && (
                        <Button
                            size="small"
                            type="button"
                            onClick={onClose}
                            variant="outlined"
                        >
                            {t('close')}
                        </Button>
                    )}
                    <Button
                        loading={isBODConfirming}
                        type="submit"
                        size="small"
                        className="min-w-[120px]"
                        disabled={
                            dayOff?.day_off_status !== 'leader-approved' ||
                            isBODConfirming
                        }
                    >
                        {t('confirm')}
                    </Button>
                </div>
            </form>
        </div>
    )
}
export const BODForm = (props: DayOffFormBODProps) => {
    return <BODFormConfirm {...props} />
}
